import { useState, useEffect } from 'react';
import getUserDetails from './utils/getUserDetails';
import { isLiveStage } from '~/util';

/**
 * To test Intercom in development, set the INTERCOM_APP_ID environment variable to 'tu5xjm2k'
 * Make sure to set it here and in the webpack config `intercom_app_id`.
 * Remove isLiveStage check to use Intercom in development.
 * Then switch to the test workspace in Intercom. Profile -> Workspace -> BNDL [TEST]
 */

const shouldMockIntercom = !process.env.INTERCOM_APP_ID || !isLiveStage();

type UserDetails = {
  id: string;
  email: string;
  name: string;
  phone: string | null | undefined;
  mainOffice: string | null | undefined;
  first_admin: boolean | null | undefined;
  signed_up_at: number | null | undefined;
  intercomHash: string;
};

type IntercomBootOptions = {
  user: UserDetails;
};

type DH_Intercom = {
  boot: (options?: IntercomBootOptions) => void;
  logOff: () => void;
  openChat: (initialMessage?: string) => void;
  update: (args: $Object) => void;
};

const printActionForDev =
  (command: string) =>
  (...args) => {
    // eslint-disable-next-line no-console
    console.info(`Mocked Intercom "${command}" called with:`, ...args);
  };

const useIntercom = () => {
  const [intercom, setIntercom] = useState<DH_Intercom>({
    boot: printActionForDev('boot'),
    logOff: printActionForDev('logOff'),
    openChat: printActionForDev('openChat'),
    update: printActionForDev('update'),
  });

  useEffect(() => {
    if (!shouldMockIntercom) {
      setIntercom({
        boot: options => {
          if ('Intercom' in global.window) {
            const userDetails =
              options && options.user ? getUserDetails(options.user) : {};

            // getUserDetails returns null for internal users (we skip Intercom
            // for them because identity verification ties the token to the
            // real userId, so we can no longer mask).
            if (userDetails === null) return;

            // api_base must point to the JWT-aware endpoint, otherwise the
            // intercom_user_jwt field is ignored and verification fails.
            global.window.Intercom('boot', {
              api_base: 'https://api-iam.intercom.io',
              app_id: process.env.INTERCOM_APP_ID,
              ...userDetails,
            });
          }
        },
        logOff: () => {
          if ('Intercom' in global.window) {
            global.window.Intercom('shutdown');
          }
        },
        openChat: initialMessage => {
          if ('Intercom' in global.window) {
            global.window.Intercom('showNewMessage', initialMessage);
          }
        },
        update: args => {
          if ('Intercom' in global.window) {
            global.window.Intercom('update', args);
          }
        },
      });
    }
  }, []);

  return intercom;
};

export default useIntercom;
